import React, { useState, KeyboardEvent, FormEvent } from 'react';
import cx from 'classnames';
import Autosuggest, { GetSuggestionValue, SuggestionSelectedEventData } from 'react-autosuggest';
import _assign from 'lodash/assign';
import { FieldInputAdaptedProps } from 'components/common/form/util/types';
import { GeoPinIcon, InputFieldV2 } from '@zola/zola-ui';
// Helpers
import {
  WMarketplaceLocationEntity,
  debouncedFetchSuggestedLocations,
} from '../../../../../../../helpers/weddingLocation';

import styles from './WeddingLocationLookahead.module.less';

type WeddingLocationLookaheadProps = {
  className?: string;
  inputProps?: FieldInputAdaptedProps;
};

// Uncontrolled input component that handles fetching of wedding location suggestions
// wrapper for react-autosuggest
const WeddingLocationLookahead: React.FC<WeddingLocationLookaheadProps> = ({
  className,
  inputProps,
}) => {
  const [suggestedLocations, setSuggestedLocations] = useState<WMarketplaceLocationEntity[]>([]);

  // Used as the value for the input
  const getSuggestionValue: GetSuggestionValue<WMarketplaceLocationEntity> = suggestion => {
    if (suggestion?.type === 'US_CITY' || suggestion?.type === 'NOT_LISTED') {
      return suggestion.name;
    }
    return '';
  };
  // Used in render suggestion
  const getSuggestionText = (location: WMarketplaceLocationEntity): string | null => {
    if (location.type === 'PLACEHOLDER') {
      return location.name;
    }
    return getSuggestionValue(location);
  };
  const renderSuggestion = (suggestion: WMarketplaceLocationEntity): JSX.Element => {
    return (
      <div className={styles.locationOption}>
        <GeoPinIcon className={styles.geoIcon} width={25} height={25} />
        {getSuggestionText(suggestion)}
      </div>
    );
  };

  const onSuggestionSelected = (
    event: FormEvent,
    { suggestion: location }: SuggestionSelectedEventData<WMarketplaceLocationEntity>
  ): void => {
    event.preventDefault();
    const { type, name } = location;

    if (type === 'US_CITY' || type === 'NOT_LISTED') {
      if (inputProps) inputProps.onChange({ target: { value: name } });
    }
  };

  const handleEnterKeyPress = (event: KeyboardEvent): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const suggestion = suggestedLocations && suggestedLocations[0];
      if (suggestion) {
        onSuggestionSelected(event, {
          suggestion,
          suggestionValue: suggestion.name,
          suggestionIndex: 0,
          sectionIndex: 0,
          method: 'enter',
        });
      }
    }
  };

  const defaultInputProps = {
    id: 'location-autosuggest',
    autoComplete: 'chrome-off',
    onKeyUp: handleEnterKeyPress,
    addOn: 'geopin',
  };
  const modifiedInputProps = _assign(defaultInputProps, inputProps);

  return (
    <div className={cx(styles.locationDropdown, className)}>
      <Autosuggest<WMarketplaceLocationEntity>
        suggestions={suggestedLocations}
        onSuggestionSelected={onSuggestionSelected}
        onSuggestionsFetchRequested={({ value }): void =>
          debouncedFetchSuggestedLocations(value, setSuggestedLocations, false)
        }
        onSuggestionsClearRequested={(): void => setSuggestedLocations([])}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={renderSuggestion}
        renderInputComponent={(props): JSX.Element => {
          return <InputFieldV2 {...props} />;
        }}
        inputProps={modifiedInputProps}
        focusInputOnSuggestionClick={false}
      />
    </div>
  );
};

export default WeddingLocationLookahead;



// WEBPACK FOOTER //
// ./src/pages/settings/wedding/WeddingDetails/components/WeddingLocationLookaheadAdapter/WeddingLocationLookahead/WeddingLocationLookahead.tsx